import React, { useEffect, useRef } from 'react';

import useStyles from './styles';
import { IVideoTrack } from 'features/video-conference/utils';
import useMediaStreamTrack from 'features/video-conference/hooks/useMediaStreamTrack';
import useVideoTrackDimensions from 'features/video-conference/hooks/useVideoTrackDimensions';
import {
  applyVideoLogic,
  cleanupVideoWorkaround,
  isIOS15,
  isSafari,
} from 'features/video-conference/utils/ios15Workaround';

interface VideoTrackProps {
  track: IVideoTrack;
  isLocal?: boolean;
}

export default function VideoTrack({ track, isLocal }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const mediaStreamTrack = useMediaStreamTrack(track);
  const dimensions = useVideoTrackDimensions(track);

  const isPortrait = (dimensions?.height ?? 0) > (dimensions?.width ?? 0);
  const isFrontFacing =
    mediaStreamTrack?.getSettings().facingMode !== 'environment';
  const shouldRotate = isLocal && isFrontFacing;
  const isContain = (isPortrait && !isLocal) || track.name.includes('screen');

  const classes = useStyles({ shouldRotate, isContain });

  useEffect(() => {
    const videoElement = ref.current;
    const shouldApplyWorkaround = isIOS15() && isSafari();

    if (!videoElement || !track) {
      return undefined;
    }

    videoElement.muted = true;
    track.attach(videoElement);

    if (shouldApplyWorkaround) {
      applyVideoLogic(track, videoElement);
    }

    return () => {
      if (shouldApplyWorkaround) {
        cleanupVideoWorkaround(videoElement);
      }
      track.detach(videoElement);
      videoElement.srcObject = null;
    };
  }, [track]);

  return (
    <video
      data-testid="video-element"
      className={classes.videoPlayer}
      ref={ref}
      playsInline
      autoPlay
    >
      <track kind="captions" />
    </video>
  );
}
